import * as React from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { Box, Button } from '@kis-technologies/kis-ui';
import { Configuration, selectors as configSelectors } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { useStyles } from './styled';
import { ForwardNavProps } from '../types';
import eventBus from 'routes/components/eventBus';

/**
 * Forward Navigation Button that is used in Footer, Keyboard and Numpad components
 */
const ForwardNavigationButton: React.FC<ForwardNavProps> = (props: ForwardNavProps) => {
  const { label, isDisabled, overflow = 'visible', testId, onClick, size = 'x-large', width, skipNavPrompt } = props;

  const hasTimerOverlay = useSelector(ticketingSelectors.selectTimerOverlay);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;

  const inactivityDuration = get(configuration, 'configuration.globalSettings.inactivityTimeout.duration', null);
  const navigationPrompt = get(configuration, 'configuration.globalSettings.inactivityTimeout.navigationPrompt');
  const navPromptEnabled = get(navigationPrompt, 'enabled');
  const zoomSpeed = get(navigationPrompt, 'buttonZoomSpeed');
  const zoomSize = get(navigationPrompt, 'buttonZoomSize');

  const interval = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const timeout = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [style, setStyle] = React.useState<React.CSSProperties>({});
  const [showTransition, setShowTransition] = React.useState(false);

  const handleTimer = () => {
    // Reset existing timers
    setShowTransition(false);
    setStyle({});

    if (interval.current) {
      clearInterval(interval.current);
      interval.current = undefined;
    } else if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
    // Navigation prompt required only when next button is present is enabled
    if (label && !timeout.current && !isDisabled && !skipNavPrompt && inactivityDuration) {
      timeout.current = setTimeout(() => {
        setShowTransition(true);
      }, inactivityDuration * 1000);
    }
  };

  React.useEffect(() => {
    // Sending "navigationPrompt" phase completed message when
    // leaving from ForwardNavigationButton component
    return () => {
      setTimeout(() => {
        eventBus.dispatch('navigationPrompt', { message: true });
      }, 0);
    };
  }, []);

  React.useEffect(() => {
    // Adding 'click' eventListener to reset the timers
    if (hasTimerOverlay) {
      document.removeEventListener('click', handleTimer, true);
      document.removeEventListener('keydown', handleTimer, true);
    } else {
      if (navPromptEnabled && inactivityDuration !== null && label) {
        document.addEventListener('click', handleTimer, true);
        document.addEventListener('keydown', handleTimer, true);
        handleTimer();
      }
    }
    return () => {
      document.removeEventListener('click', handleTimer, true);
      document.removeEventListener('keydown', handleTimer, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled, hasTimerOverlay, skipNavPrompt]);

  React.useEffect(() => {
    if (showTransition) {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }
      if (zoomSpeed) {
        interval.current = setInterval(() => {
          if (zoomSize && zoomSize !== 1) {
            setStyle((prevState) => (Object.keys(prevState).length ? {} : { transform: `scale(${zoomSize})` }));
          }
        }, zoomSpeed * 1000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTransition]);

  React.useEffect(() => {
    // Dispatch 'navigationPrompt' to handle inactivityTimer overlay
    if (!navPromptEnabled || showTransition || !label || (label && isDisabled) || skipNavPrompt) {
      setTimeout(() => {
        eventBus.dispatch('navigationPrompt', { message: true });
      }, 0);
    } else {
      setTimeout(() => {
        eventBus.dispatch('navigationPrompt', { message: false });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTransition, isDisabled, skipNavPrompt]);

  const classes = useStyles({ zoomSpeed, showTransition })();

  return (
    <>
      {label && (
        <Box flexOverride="none" overflow={overflow}>
          <Button
            onClick={onClick}
            disabled={isDisabled}
            appearance="primary"
            customSize={size}
            endIconName="next_circle"
            data-testid={testId}
            className={classes.forwardButton}
            sx={style}
            width={width ? width : 'undefined'}
          >
            {label}
          </Button>
        </Box>
      )}
    </>
  );
};

export default ForwardNavigationButton;
